import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity, sanitizePayload } from '../../utils/security';
import { toast } from 'react-toastify';
import {
    ArrowLeft, Users, Building2, User, Mail, Phone, Globe,
    DollarSign, CheckCircle, Save, X, Sparkles, Video, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const STATUS_OPTIONS = [
    { value: 'lead', label: '🔴 ლიდი / საწყისი კონტაქტი', color: '#ef4444' },
    { value: 'meeting_scheduled', label: '🟡 შეხვედრა დაგეგმილია', color: '#eab308' },
    { value: 'negotiation', label: '🟡 მოლაპარაკება პროცესშია', color: '#eab308' },
    { value: 'offer_sent', label: '🔵 შეთავაზება გაგზავნილია', color: '#3b82f6' },
    { value: 'contract_signed', label: '🟢 კონტრაქტი გაფორმდა (წარმატებული)', color: '#22c55e' },
    { value: 'rejected', label: '🔴 უარყოფილია / შეჩერებულია', color: '#94a3b8' },
];

const SECTORS = [
    { value: 'business', label: '🏢 კერძო ბიზნესი / Enterprise' },
    { value: 'gov', label: '🏛️ საჯარო / მუნიციპალური / სახელმწიფო' },
    { value: 'education', label: '🎓 განათლება & უნივერსიტეტი' },
    { value: 'healthcare', label: '🏥 მედიცინა & ჯანდაცვა' },
    { value: 'fintech', label: '💳 ფინტექი & საბანკო სექტორი' },
    { value: 'ngo', label: '🌐 არასამთავრობო / საერთაშორისო ფონდი' },
    { value: 'other', label: '📦 სხვა ინდუსტრია' },
];

const OFFER_TYPES = [
    { value: 'custom_solution', label: '💻 ინდივიდუალური პროგრამული უზრუნველყოფა (Custom)' },
    { value: 'municipal_suite', label: '🏛️ მუნიციპალური ციფრული პლატფორმა' },
    { value: 'saas_license', label: '☁️ SaaS გამოწერა & ყოველწლიური ლიცენზია' },
    { value: 'automation_ai', label: '⚡ ბიზნეს ავტომატიზაცია & AI ინტეგრაცია' },
    { value: 'consulting_audit', label: '🔍 IT აუდიტი & ტექნიკური კონსულტაცია' },
];

const AdminPotentialCustomerCreate = () => {
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const isKa = i18n.language === 'ka';

    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        sector: 'business',
        status: 'lead',
        priority: 'medium',
        contact_person: '',
        contact_position: '',
        contact_email: '',
        contact_phone: '',
        website: '',
        client_wants: '',
        our_offer: '',
        offer_type: 'custom_solution',
        estimated_budget: '',
        meeting_link: '',
        notes: '',
        autonomous_mode: 1
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            toast.error(isKa ? 'გთხოვთ მიუთითოთ ორგანიზაციის / კლიენტის სახელი' : 'Please provide organization name');
            return;
        }

        setSaving(true);
        try {
            const cleanPayload = sanitizePayload({
                ...formData,
                estimated_budget: formData.estimated_budget ? parseFloat(formData.estimated_budget) : 0
            });

            const res = await fetchWithSecurity(`${API_URL}customers.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(cleanPayload)
            });

            const data = await res.json();
            if (data.success) {
                toast.success(isKa ? 'ახალი კლიენტი წარმატებით შეიქმნა!' : 'Client created successfully!');
                if (data.id) {
                    navigate(`/admin/potential-customers/${data.id}`);
                } else {
                    navigate('/admin/potential-customers');
                }
            } else {
                toast.error(data.error || (isKa ? 'შექმნა ვერ მოხერხდა' : 'Failed to create client'));
            }
        } catch (err) {
            toast.error(isKa ? 'შეცდომა სერვერთან კავშირისას' : 'Network error');
        }
        setSaving(false);
    };

    if (authLoading || !user) return null;
    if (!isSuperAdmin()) {
        navigate('/admin');
        return null;
    }

    return (
        <AdminLayout title={isKa ? 'ახალი პოტენციური კლიენტის დამატება' : 'Add New Potential Customer'}>
            <div className="container-fluid px-0">
                {/* ── Top Header Navigation ── */}
                <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2 pb-3 border-bottom">
                    <div className="d-flex align-items-center gap-3">
                        <Link 
                            to="/admin/potential-customers"
                            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1 rounded-pill px-3"
                        >
                            <ArrowLeft size={16} />
                            <span>{isKa ? 'კლიენტების სია' : 'Back to Leads'}</span>
                        </Link>
                        <div>
                            <h3 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                                <Users size={24} className="text-primary" />
                                <span>{isKa ? 'ახალი პოტენციური კლიენტის პროფილი' : 'Create Potential Client Profile'}</span>
                            </h3>
                            <p className="text-muted small mb-0">
                                {isKa 
                                    ? 'შეიყვანეთ კლიენტის მოთხოვნები, საკონტაქტო პირი, ბიუჯეტი და Orbitronix-ის სავარაუდო შეთავაზება' 
                                    : 'Enter client requirements, contact details, estimated budget, and proposal roadmap'}
                            </p>
                        </div>
                    </div>

                    <div className="d-flex gap-2">
                        <button 
                            type="button" 
                            className="btn btn-light border btn-sm px-3"
                            onClick={() => navigate('/admin/potential-customers')}
                            disabled={saving}
                        >
                            <X size={15} className="me-1" />
                            {isKa ? 'გაუქმება' : 'Cancel'}
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-primary btn-sm px-4 fw-semibold d-inline-flex align-items-center gap-2"
                            onClick={handleSubmit}
                            disabled={saving}
                        >
                            {saving ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                            ) : (
                                <Save size={16} />
                            )}
                            <span>{isKa ? 'პროფილის შექმნა' : 'Create Profile'}</span>
                        </button>
                    </div>
                </div>

                {/* ── Main Form Grid ── */}
                <form onSubmit={handleSubmit}>
                    <div className="row g-4">
                        {/* LEFT COLUMN: Core Details & Contacts */}
                        <div className="col-12 col-lg-7">
                            {/* Card 1: Primary Organization Info */}
                            <div className="card shadow-sm border-0 mb-4 rounded-3">
                                <div className="card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
                                    <Building2 size={18} className="text-primary" />
                                    <h5 className="card-title mb-0 fw-bold">{isKa ? '1. ორგანიზაციის ძირითადი მონაცემები' : '1. Organization Details'}</h5>
                                </div>
                                <div className="card-body p-4">
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <label className="form-label fw-semibold small text-dark">
                                                {isKa ? 'ორგანიზაციის / კომპანიის სახელი *' : 'Organization / Company Name *'}
                                            </label>
                                            <input 
                                                type="text" 
                                                name="name" 
                                                className="form-control"
                                                placeholder={isKa ? 'მაგ: ბორჯომის მუნიციპალიტეტი, შპს ჯორჯიან ტექნოლოჯიზ...' : 'e.g. Borjomi Municipality, Georgian Tech LLC...'}
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                autoFocus
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold small text-dark">
                                                {isKa ? 'სექტორი / ინდუსტრია' : 'Sector / Industry'}
                                            </label>
                                            <select 
                                                name="sector" 
                                                className="form-select"
                                                value={formData.sector}
                                                onChange={handleChange}
                                            >
                                                {SECTORS.map(s => (
                                                    <option key={s.value} value={s.value}>{s.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold small text-dark">
                                                {isKa ? 'მიმდინარე სტატუსი (Funnel)' : 'Lead Pipeline Status'}
                                            </label>
                                            <select 
                                                name="status" 
                                                className="form-select"
                                                value={formData.status}
                                                onChange={handleChange}
                                            >
                                                {STATUS_OPTIONS.map(st => (
                                                    <option key={st.value} value={st.value}>{st.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold small text-dark">
                                                {isKa ? 'პრიორიტეტი' : 'Priority'}
                                            </label>
                                            <select 
                                                name="priority" 
                                                className="form-select"
                                                value={formData.priority}
                                                onChange={handleChange}
                                            >
                                                <option value="high">🔴 {isKa ? 'მაღალი (High Focus)' : 'High Priority'}</option>
                                                <option value="medium">🟡 {isKa ? 'საშუალო (Medium)' : 'Medium Priority'}</option>
                                                <option value="low">🟢 {isKa ? 'დაბალი (Low / Later)' : 'Low Priority'}</option>
                                            </select>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold small text-dark">
                                                {isKa ? 'ვებსაიტი / ბმული' : 'Website URL'}
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light text-muted"><Globe size={16} /></span>
                                                <input 
                                                    type="text" 
                                                    name="website" 
                                                    className="form-control"
                                                    placeholder="https://example.ge"
                                                    value={formData.website}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Card 2: Contact Person */}
                            <div className="card shadow-sm border-0 mb-4 rounded-3">
                                <div className="card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
                                    <User size={18} className="text-info" />
                                    <h5 className="card-title mb-0 fw-bold">{isKa ? '2. საკონტაქტო პირი & კომუნიკაცია' : '2. Key Contact Person'}</h5>
                                </div>
                                <div className="card-body p-4">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold small text-dark">
                                                {isKa ? 'საკონტაქტო პირის სახელი & გვარი' : 'Contact Person Full Name'}
                                            </label>
                                            <input 
                                                type="text" 
                                                name="contact_person" 
                                                className="form-control"
                                                placeholder={isKa ? 'მაგ: გიორგი ბერიძე' : 'e.g. John Doe'}
                                                value={formData.contact_person}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold small text-dark">
                                                {isKa ? 'თანამდებობა / როლი' : 'Position / Title'}
                                            </label>
                                            <input 
                                                type="text" 
                                                name="contact_position" 
                                                className="form-control"
                                                placeholder={isKa ? 'მაგ: IT დეპარტამენტის ხელმძღვანელი' : 'e.g. Head of Procurement'}
                                                value={formData.contact_position}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold small text-dark">
                                                {isKa ? 'ელექტრონული ფოსტა' : 'Contact Email'}
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light text-muted"><Mail size={16} /></span>
                                                <input 
                                                    type="email" 
                                                    name="contact_email" 
                                                    className="form-control"
                                                    placeholder="contact@company.ge"
                                                    value={formData.contact_email}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold small text-dark">
                                                {isKa ? 'ტელეფონის ნომერი' : 'Phone Number'}
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light text-muted"><Phone size={16} /></span>
                                                <input 
                                                    type="text" 
                                                    name="contact_phone" 
                                                    className="form-control"
                                                    placeholder="+995 599 00 00 00"
                                                    value={formData.contact_phone}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Client Wants, Orbitronix Proposal, Financials */}
                        <div className="col-12 col-lg-5">
                            {/* Card 3: Requirements & Tailored Solution */}
                            <div className="card shadow-sm border-0 mb-4 rounded-3" style={{ borderTop: '4px solid #6366f1' }}>
                                <div className="card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
                                    <Sparkles size={18} className="text-indigo" style={{ color: '#6366f1' }} />
                                    <h5 className="card-title mb-0 fw-bold">{isKa ? '3. მოთხოვნა & Orbitronix-ის შეთავაზება' : '3. Requirements & Offer'}</h5>
                                </div>
                                <div className="card-body p-4">
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold small text-dark">
                                            {isKa ? 'კლიენტის მოთხოვნა / რა სურს კლიენტს?' : 'Client Requirements / What They Want'}
                                        </label>
                                        <textarea 
                                            name="client_wants" 
                                            className="form-control"
                                            rows="4"
                                            placeholder={isKa 
                                                ? 'აღწერეთ კლიენტის არსებული გამოწვევები, სასურველი მოდულები და ტექნიკური საჭიროებები...' 
                                                : 'Describe client pain points, desired modules, and specific requirements...'}
                                            value={formData.client_wants}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-semibold small text-dark">
                                            {isKa ? 'შეთავაზების ტიპი' : 'Offer Type'}
                                        </label>
                                        <select 
                                            name="offer_type" 
                                            className="form-select"
                                            value={formData.offer_type}
                                            onChange={handleChange}
                                        >
                                            {OFFER_TYPES.map(ot => (
                                                <option key={ot.value} value={ot.value}>{ot.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-semibold small text-dark">
                                            {isKa ? 'Orbitronix-ის შეთავაზება / გადაწყვეტა' : 'Orbitronix Tailored Solution'}
                                        </label>
                                        <textarea 
                                            name="our_offer" 
                                            className="form-control"
                                            rows="4"
                                            placeholder={isKa 
                                                ? 'პროდუქტები, პაკეტები, ინტეგრაციის გეგმა ან შეთავაზებული არქიტექტურა...' 
                                                : 'Proposed software modules, packages, rollout plan...'}
                                            value={formData.our_offer}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-semibold small text-dark">
                                            {isKa ? 'სავარაუდო ბიუჯეტი (₾ / $)' : 'Estimated Budget (GEL / USD)'}
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light text-muted"><DollarSign size={16} /></span>
                                            <input 
                                                type="number" 
                                                name="estimated_budget" 
                                                className="form-control"
                                                placeholder="25000"
                                                value={formData.estimated_budget}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-semibold small text-dark">
                                            {isKa ? 'დაგეგმილი შეხვედრის ბმული (Google Meet / Zoom)' : 'Planned Meeting Link'}
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light text-muted"><Video size={16} /></span>
                                            <input 
                                                type="text" 
                                                name="meeting_link" 
                                                className="form-control"
                                                placeholder="https://meet.google.com/xyz-abc"
                                                value={formData.meeting_link}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="form-label fw-semibold small text-dark">
                                            {isKa ? 'დამატებითი შენიშვნები & შიდა კომენტარი' : 'Internal Notes'}
                                        </label>
                                        <textarea 
                                            name="notes" 
                                            className="form-control"
                                            rows="3"
                                            placeholder={isKa ? 'შიდა შენიშვნები გუნდისთვის...' : 'Internal notes for the team...'}
                                            value={formData.notes}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button Card */}
                            <div className="card shadow-sm border-0 rounded-3 bg-light p-3">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div className="small text-muted d-flex align-items-center gap-1">
                                        <Shield size={14} className="text-success" />
                                        <span>{isKa ? 'მონაცემები ინახება დაცულად' : 'Protected & Encrypted'}</span>
                                    </div>
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary px-4 fw-bold d-inline-flex align-items-center gap-2"
                                        disabled={saving}
                                    >
                                        {saving ? (
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                        ) : (
                                            <CheckCircle size={16} />
                                        )}
                                        <span>{isKa ? 'შენახვა & გახსნა' : 'Save & View Profile'}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </AdminLayout>
    );
};

export default AdminPotentialCustomerCreate;
